import { getAuthData } from "@common/helpers/auth";
import { swarmRequest } from "@common/helpers/util/requestMiddleware";
import { LoggedIn } from "../actions/login";
import { UserDidLogin } from "../actions/terminal";
import { store } from "../index/terminalApp";
import { checkFreeBonuses } from "./freeBonuses";

// Persist the minimum credentials we need to re-establish a player session
// after a page reload. Kept in `localStorage` (NOT sessionStorage) so the
// terminal stays signed in across full refreshes / Kespay redirects /
// closing-and-reopening the tab — bootstrap calls `restoreLogin` if the
// pair is present and silently logs the user back in.
const RESTORE_LOGIN_STORAGE_KEY = 'restoreLoginCreds';

const saveRestoreLoginCreds = (authData) => {
  if (!authData?.auth_token || authData?.user_id == null) return;
  try {
    localStorage.setItem(
      RESTORE_LOGIN_STORAGE_KEY,
      JSON.stringify({
        auth_token: authData.auth_token,
        user_id: authData.user_id,
      }),
    );
  } catch (_) { /* quota / disabled storage — non-fatal */ }
};

export const clearRestoreLoginCreds = () => {
  try {
    localStorage.removeItem(RESTORE_LOGIN_STORAGE_KEY);
  } catch (_) { /* see above */ }
};

const getRestoreLoginCreds = () => {
  try {
    const raw = localStorage.getItem(RESTORE_LOGIN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.auth_token || parsed?.user_id == null) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

export const makeLogin = (user) => {
  store.dispatch(LoggedIn(user));
  store.dispatch(UserDidLogin({ userId: user.authData.user_id, currency: getAuthData().currency }));
  saveRestoreLoginCreds(user.authData);
  checkFreeBonuses();
}

// Re-establish the player session on app boot. If `{auth_token, user_id}`
// is stashed from a previous successful login, fires `restore_login` on
// the open swarm and — on success — feeds the response into the same
// `makeLogin` pipeline that the regular `login` action uses. The shape
// of the response is identical to `login` (auth_token, user_id, deposit
// stats, etc.), so downstream consumers don't need to know it came from
// a restore.
//
// On any failure (no creds, bad creds, network error) the stash is
// cleared so we don't get stuck retrying an expired token every reload.
export const restoreLogin = async () => {
  const creds = getRestoreLoginCreds();
  if (!creds) return null;

  try {
    const response = await swarmRequest(
      { auth_token: creds.auth_token, user_id: creds.user_id },
      'restore_login',
    );

    // swarmRequest convention: truthy `response.status` means an error
    // payload (same check the regular `login` action uses). A real
    // restore returns the login-shaped object with `auth_token`/`user_id`.
    if (!response || response.status || !response.auth_token) {
      clearRestoreLoginCreds();
      return null;
    }

    makeLogin({ authData: response });
    return response;
  } catch {
    clearRestoreLoginCreds();
    return null;
  }
};
